import { Field, FieldArray, FieldProps, Formik, FormikProps } from 'formik'
import React, { useState } from 'react'
import Select from 'react-select'
import ReactTooltip from 'react-tooltip'
import { Button } from '@mobiscroll/react-latest'
import { ServicePromoFareIntervalOptions, ServicePromoFareIntervals } from '../../../constants/service-promo-fare'
import { discountTypeOptions } from 'src/constants/service'
import { IAddPaymentMethod, IPaymentMethods, IServicePricingDiscount, IStoreState } from '../../../types/index'
import {
  IServicePricingCreateDTO,
  IServicePricingDiscountDTO,
  IServicePricingModel,
  IServicePricingUpdateDTO,
  IServicePricingUpdatePayload
} from '../../../types/service-pricing'
import Toggle from '../../ui/Toggle'
import * as Yup from 'yup'
import { yupNumberStrong } from '../../../utils/yup'
import { generateRandomString } from 'src/utils/generate'
import { parseDiscountLabel } from 'src/utils'
import TooltipIcon from 'src/components/icons/ui/TooltipIcon'

interface IServicePricingBreakdownProps {
  servicePricingDiscount: IServicePricingDiscount
  formRef: any
  serviceId?: string
  orgId: string
  pricing?: IServicePricingModel
  paymentMethods: IPaymentMethods[]
  onCreateServiceCompleted?: () => void
  updatePricing: (pricing: IServicePricingUpdateDTO, isServicePricingDiscountEnable: boolean) => Promise<any>
  createPricing: (pricing: IServicePricingCreateDTO) => Promise<any>
  onComponentMount: (serviceId: string, orgId: string) => void
  updatePaymentMethods: (serviceId: string, paymentMethods: IPaymentMethods[]) => Promise<any>
  addPaymentMethod: (serviceId: string, paymentMethod: IPaymentMethods[]) => Promise<any>
  fetchServicePricingDiscount: (serviceId: string) => Promise<any>
  updateServicePricingDiscounts: (serviceId: string, payload: IServicePricingUpdatePayload) => Promise<any>
}

interface IServicePricingBreakdownState {
  isServicePricingDiscountEnable: boolean
  pricingDiscounts: IServicePricingDiscountDTO[]
  showLess: boolean
}

const MONEY_FIELDS = [
  'fare',
  'promoFare',
  'commissionFlat',
  'discountFlat',
  'commissionPercentage',
  'discountPercentage',
  'discount',
  'stateTax',
  'commission'
]
declare module 'yup' {
  // tslint:disable-next-line
  interface ArraySchema<T> {
    unique(message: string, mapper: (a: T) => T): ArraySchema<T>
  }
}
export default class ServicePricingBreakdown extends React.Component<
  IServicePricingBreakdownProps,
  IServicePricingBreakdownState
> {
  private pricingValidationSchema: any

  constructor(props: IServicePricingBreakdownProps) {
    super(props)
    this.loadYupValidation()
    this.state = {
      isServicePricingDiscountEnable: false,
      pricingDiscounts: [],
      showLess: false
    }
  }

  public componentDidMount() {
    if (this.props.serviceId) {
      this.props.onComponentMount(this.props.serviceId, this.props.orgId)
      this.props.fetchServicePricingDiscount(this.props.serviceId)
    }
  }

  public componentDidUpdate(
    prevProps: Readonly<IServicePricingBreakdownProps>,
    prevState: Readonly<IServicePricingBreakdownState>,
    snapshot?: any
  ): void {
    const { data: pricingDiscounts } = this.props.servicePricingDiscount
    const { data: prevPricingDiscounts } = prevProps.servicePricingDiscount
    if (pricingDiscounts !== prevPricingDiscounts) {
      const sortedData = [...pricingDiscounts].sort((a, b) => a.componentName.localeCompare(b.componentName))
      this.setState({
        pricingDiscounts: sortedData.length > 8 ? sortedData.slice(0, 8) : sortedData,
        showLess: sortedData.length > 8
      })
    }
  }

  public loadYupValidation() {
    Yup.addMethod(Yup.mixed, 'unique', function(message, mapper = (a: any) => a) {
      return this.test('unique', message, (list: any) => {
        const valueArr = list.map((item: any) => item.paymentName)
        const hasDuplicate = valueArr.some((item: any, idx: any) => {
          return valueArr.indexOf(item) !== idx
        })
        if (hasDuplicate) {
          return false
        }
        return true
      })
    })

    this.pricingValidationSchema = Yup.object().shape({
      fare: yupNumberStrong()
        .min(0, 'Wrong value')
        .max(1000, 'Wrong value'),
      promoFare: yupNumberStrong()
        .min(0, 'Wrong value')
        .max(1000, 'Wrong value'),
      promoLimit: yupNumberStrong()
        .min(0, 'Wrong value')
        .max(1000, 'Wrong value'),
      commission: yupNumberStrong()
        .min(0, 'Wrong value')
        .max(100, 'Wrong value'),
      commissionFlat: yupNumberStrong()
        .min(0, 'Wrong value')
        .max(1000, 'Wrong value'),
      discountFlat: yupNumberStrong()
        .min(0, 'Wrong value')
        .max(10000, 'Wrong value'),
      discount: yupNumberStrong()
        .min(0, 'Wrong value')
        .max(100, 'Wrong value'),
      stateTax: yupNumberStrong()
        .min(0, 'Wrong value')
        .max(1, 'Wrong value'),
      federalTax: yupNumberStrong()
        .min(0, 'Wrong value')
        .max(100, 'Wrong value'),
      paymentMethods: Yup.array()
        .of(
          Yup.object().shape({
            paymentName: Yup.string()
              .min(2, 'payment name too short')
              .required('Required')
          })
        )
        .unique('duplicatePaymentName', (a: any) => a)
    })
  }
  public render() {
    if (!this.props.serviceId) {
      return null
    }

    const { pricingDiscounts } = this.state
    const { data: originalPricingDiscountsData } = this.props.servicePricingDiscount
    const pricingFormValues = this.convertToFormValues(this.props.pricing)
    const pricing = this.convertPricingValuesToDollars(pricingFormValues)

    return (
      <div className='border-box px2 col-12 mb3'>
        <Formik
          ref={this.props.formRef}
          enableReinitialize={true}
          initialValues={{
            isManualCollectEnabled: pricing ? pricing.isManualCollectEnabled : false,
            fare: pricing ? pricing.fare : undefined,
            promoFare: pricing ? pricing.promoFare : undefined,
            promoEnabled: !!(pricing && pricing.promoLimit),
            promoLimit: pricing ? pricing.promoLimit : undefined,
            promoDuration: pricing ? pricing.promoDuration : ServicePromoFareIntervals.MONTH,
            commissionFlat: pricing ? pricing.commissionFlat : undefined,
            commission: pricing ? pricing.commission : undefined,
            discountFlat: pricing ? pricing.discountFlat : undefined,
            discount: pricing ? pricing.discount : undefined,
            stateTax: pricing ? pricing.stateTax : undefined,
            federalTax: pricing ? pricing.federalTax : undefined,
            freeAttendant: false,
            paymentMethods: this.props.paymentMethods,
            serviceId: this.props.serviceId,
            isRemoveCentsEnabled: pricing ? pricing.isRemoveCentsEnabled : false,
            originalPricingDiscountsData: originalPricingDiscountsData.length ? originalPricingDiscountsData : [],
            pricingDiscounts: pricingDiscounts.length ? pricingDiscounts : [],
            showLess: this.state.showLess
          }}
          validationSchema={this.pricingValidationSchema}
          onSubmit={this.handleFormikSubmit}
          render={InnerForm(
            this.servicePricingDiscountChange,
            this.handleDisableAllDiscount,
            this.handleDiscountChange,
            this.handleDiscountSlicing,
            this.handleEnableDiscountToggle
          )}
        />
      </div>
    )
  }

  private servicePricingDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target
    if (checked) {
      this.setState({ isServicePricingDiscountEnable: checked })
    }
  }

  private getIndex = (id: string) => {
    const index = this.state.pricingDiscounts.findIndex(element => element.id === id)
    return index
  }

  private getHiddenDiscountDetails = () => {
    const { servicePricingDiscount } = this.props
    if (servicePricingDiscount.data.length > 8) {
      const remainingDiscountDetails = [...servicePricingDiscount.data]
        .sort((a, b) => a.componentName.localeCompare(b.componentName))
        .slice(8, servicePricingDiscount.data.length)
      return remainingDiscountDetails
    }
    return []
  }

  private handleDisableAllDiscount = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const { pricingDiscounts } = this.state
    const disableAllDiscountsArr = pricingDiscounts.map(discount => ({ ...discount, isEnabled: false }))
    this.setState({ pricingDiscounts: disableAllDiscountsArr })
  }

  private handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    const updatedPricingDiscounts = [...this.state.pricingDiscounts]
    const { value } = e.target
    const index = this.getIndex(id)
    updatedPricingDiscounts[index].discountValue = value
    this.setState({ pricingDiscounts: updatedPricingDiscounts })
  }

  private handleEnableDiscountToggle = (value: boolean, id: string) => {
    const updatedPricingDiscounts = [...this.state.pricingDiscounts]
    const index = this.getIndex(id)
    updatedPricingDiscounts[index].isEnabled = value
    this.setState({ pricingDiscounts: updatedPricingDiscounts })
  }

  private handleDiscountSlicing = () => {
    const { servicePricingDiscount } = this.props
    if (servicePricingDiscount.data.length > 8) {
      this.setState({ showLess: !this.state.showLess }, () => {
        const { showLess, pricingDiscounts } = this.state
        if (showLess) {
          this.setState({ pricingDiscounts: [...pricingDiscounts].slice(0, 8) })
        } else {
          this.setState({ pricingDiscounts: [...pricingDiscounts, ...this.getHiddenDiscountDetails()] })
        }
      })
    }
  }

  private convertPricingValuesToDollars(pricing?: InnerFormValues) {
    if (!pricing) {
      return
    }

    const clone = { ...pricing }
    MONEY_FIELDS.forEach((field: string) => {
      if (clone[field]) {
        clone[field] = clone[field] / 100
      }
    })
    return clone
  }

  private convertPricingValuesToCents(pricing?: InnerFormValues) {
    if (!pricing) {
      return
    }

    const clone = { ...pricing }
    MONEY_FIELDS.forEach((field: string) => {
      if (clone[field]) {
        clone[field] = clone[field] * 100
      }
    })
    return clone
  }

  private convertToFormValues(pricing?: IServicePricingModel) {
    if (!pricing) {
      return
    }

    return {
      isManualCollectEnabled: pricing.isManualCollectEnabled,
      fare: pricing.fare,
      promoFare: pricing.promoFare,
      promoLimit: pricing.promoLimit,
      promoDuration: pricing.promoDuration,
      commissionFlat: pricing.commissionFlat,
      commission: pricing.commissionPercentage,
      discountFlat: pricing.discountFlat,
      discount: pricing.discountPercentage,
      stateTax: pricing.stateTax,
      federalTax: pricing.federalTax,
      freeAttendant: false,
      promoEnabled: false,
      serviceId: pricing.serviceId,
      isRemoveCentsEnabled: pricing.isRemoveCentsEnabled
    }
  }

  private handleFormikSubmit = async (values: InnerFormValues) => {
    const converted = this.convertPricingValuesToCents(values)

    if (!values || !converted) {
      return
    }

    if (this.props.pricing) {
      this.updateServicePricing(converted)
    } else {
      this.createServicePricing(converted)
    }
  }

  private createServicePricing = (values: InnerFormValues) => {
    if (!this.props.serviceId) {
      return
    }
    const pricing: IServicePricingCreateDTO = {
      orgId: this.props.orgId,
      serviceId: this.props.serviceId,
      isManualCollectEnabled: values.isManualCollectEnabled,
      fare: values.fare,
      promoFare: values.promoFare,
      promoLimit: values.promoLimit,
      promoDuration: values.promoDuration,
      commissionFlat: values.commissionFlat,
      commissionPercentage: values.commission,
      discountFlat: values.discountFlat,
      discountPercentage: values.discount,
      stateTax: values.stateTax,
      federalTax: values.federalTax,
      paymentMethods: values.paymentMethods,
      isRemoveCentsEnabled: values.isRemoveCentsEnabled
    }

    this.props
      .createPricing(pricing)
      .then(() => this.props.onCreateServiceCompleted && this.props.onCreateServiceCompleted())

    const paymentMethods = values.paymentMethods ? values.paymentMethods : ([] as IPaymentMethods[])
    this.props.addPaymentMethod(pricing.serviceId, paymentMethods)
  }

  private updateServicePricing = (values: InnerFormValues) => {
    if (!this.props.serviceId || !this.props.pricing) {
      return
    }
    const pricing: IServicePricingUpdateDTO = {
      id: this.props.pricing.id,
      orgId: this.props.orgId,
      serviceId: this.props.serviceId,
      isManualCollectEnabled: values.isManualCollectEnabled,
      fare: values.fare,
      promoFare: values.promoFare,
      promoLimit: values.promoLimit,
      promoDuration: values.promoDuration,
      commissionFlat: values.commissionFlat,
      commissionPercentage: values.commission,
      discountFlat: values.discountFlat,
      discountPercentage: values.discount,
      stateTax: values.stateTax,
      federalTax: values.federalTax,
      paymentMethods: values.paymentMethods,
      isRemoveCentsEnabled: values.isRemoveCentsEnabled
    }

    // Service Pricing Discounts
    const { pricingDiscounts } = this.state
    if (this.props.servicePricingDiscount.data !== pricingDiscounts) {
      const discounts = pricingDiscounts.map(({ id, componentName, discountValue, isEnabled }) => ({
        id,
        componentName,
        discountValue,
        isEnabled
      }))
      const payload = { discounts }
      this.props.updateServicePricingDiscounts(this.props.serviceId, payload)
    }

    this.props
      .updatePricing(pricing, this.state.isServicePricingDiscountEnable)
      .then(() => this.props.onCreateServiceCompleted && this.props.onCreateServiceCompleted())

    const paymentMethods = values.paymentMethods ? values.paymentMethods : ([] as IPaymentMethods[])
    this.props.updatePaymentMethods(pricing.serviceId, paymentMethods)
  }
}

interface InnerFormValues {
  isManualCollectEnabled: boolean
  fare: number
  promoFare: number
  promoLimit: number
  promoDuration: string
  commissionFlat: number
  commission: number
  discountFlat: number
  discount: number
  stateTax: number
  federalTax: number
  freeAttendant: boolean
  promoEnabled: boolean
  paymentMethods?: IPaymentMethods[]
  serviceId: string
  isRemoveCentsEnabled: boolean
  originalPricingDiscountsData?: IServicePricingDiscountDTO[]
  pricingDiscounts?: IServicePricingDiscountDTO[]
  showLess?: boolean
}

const selectInput = ({ field, form }: FieldProps<any>) => {
  const handleChange = (selected: any) => {
    form.setFieldValue(field.name, selected.value)
  }
  const selectedOption = ServicePromoFareIntervalOptions.find(m => m.value === field.value)
  return (
    <Select
      className='svp-promotional-fare-schedule'
      value={selectedOption}
      options={ServicePromoFareIntervalOptions}
      onChange={handleChange}
    />
  )
}

const InnerForm = (
  servicePricingDiscountChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  handleDisableAllDiscount: (e: React.MouseEvent<HTMLButtonElement>) => void,
  handleDiscountChange: (e: React.ChangeEvent<HTMLInputElement>, id: string) => void,
  handleDiscountSlicing: () => void,
  handleEnableDiscountToggle: (value: boolean, id: string) => void
) => (props: FormikProps<InnerFormValues>) => {
  const { values, touched, errors, handleSubmit, setFieldValue } = props
  const { originalPricingDiscountsData, pricingDiscounts, promoEnabled, showLess } = values

  const freeAttendantField = ({ field, form }: FieldProps<InnerFormValues>) => (
    <Toggle state={field.value} onClick={setFieldValueCreator({ field, form })} />
  )

  const setFieldValueCreator = ({ field, form }: FieldProps<InnerFormValues>) => () => {
    form.setFieldValue(field.name, !field.value)
  }

  const togglePromoFare = (ev: any) => {
    setFieldValue('promoEnabled', ev.target.checked)
    if (ev.target.checked) {
      setFieldValue('promoLimit', 1)
    } else {
      setFieldValue('promoLimit', 0)
      setFieldValue('promoFare', 0)
    }
  }

  const setManualCollectEnabled = () => {
    setFieldValue('isManualCollectEnabled', !values.isManualCollectEnabled)
  }

  const setRemoveCentsEnabled = () => {
    setFieldValue('isRemoveCentsEnabled', !values.isRemoveCentsEnabled)
  }

  const togglePaymentMethod = (i: number, activate: boolean) => {
    setFieldValue(`paymentMethods[${i}].activated`, !activate)
  }
  const addPaymentMethod = (serviceId: string, push: any) => {
    const inputElement = document.getElementById(`newPaymentMethod`) as HTMLInputElement
    push({ id: generateRandomString(9), serviceId, paymentName: inputElement.value, activated: true })
    inputElement.value = ''
  }
  const paymentNameHasErrors = (touched: any, errors: any, i: number) => {
    return (
      touched.paymentMethods &&
      errors.paymentMethods &&
      errors.paymentMethods.length > 0 &&
      errors.paymentMethods[i] &&
      !errors.paymentName
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className='bold font-dark-grey mt3'>Payment Types</div>
      <FieldArray name='paymentMethods'>
        {({ insert, remove, push }) => (
          <div>
            <div className='flex flex-column mt1'>
              {values.paymentMethods ? (
                values.paymentMethods.map((paymentMethod, i) => (
                  <div className='flex flex-row mt1 items-center' key={paymentMethod.id}>
                    <div className='col-4 flex flex-column'>
                      {/* <span onClick={() => setPaymentMethodName(i, paymentMethod.paymentName)}><EditIcon className='icon--small svg-stroke-grey3 mr1' /></span> */}
                      {/* <CrossIcon className='icon--small svg-stroke-grey3 mr1' onClick={closePaymentMethod}/> */}
                      <Field
                        name={`paymentMethods[${i}].paymentName`}
                        className='rounded p1 mr2 border-light-grey4 h4'
                      />
                      {/* <div className='font-red h6'>{paymentNameHasErrors(touched, errors, i) && <div>{errors.paymentMethods[i].paymentName}</div>}</div> */}
                      <div className='font-red h6'>
                        {paymentNameHasErrors(touched, errors, i) && <div>{errors.paymentMethods[i].paymentName}</div>}
                      </div>
                    </div>
                    <div className='col-2 flex flex-column'>
                      <Toggle
                        state={paymentMethod.activated}
                        onClick={() => togglePaymentMethod(i, paymentMethod.activated)}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div />
              )}
              <div>
                <div className='mt1 font-red h5'>
                  {typeof errors.paymentMethods === 'string'
                    ? 'Please change/remove duplicate payment method name!'
                    : ''}
                </div>
              </div>
            </div>
            <div className='flex flex-column mt2'>
              <div className='flex flex-row items-center'>
                <div className='col-4 flex flex-column'>
                  <input
                    name='newPaymentMethod'
                    placeholder='Payment Type'
                    className='rounded p1 mr2 border-light-grey4 h4'
                    id='newPaymentMethod'
                  />
                </div>
                <div className='flex flex-column pr2'>
                  <span
                    onClick={() => addPaymentMethod(values.serviceId, push)}
                    style={{ boxShadow: '0px 0px 2px grey' }}
                    className='AddServiceZoneFloatingButton rounded flex btn bg-blue h5 mr1 ml1 font-white '
                  >
                    + Add
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </FieldArray>
      <div className='bold font-dark-grey mt3'>Choose Price modifier</div>
      <div className='flex mt1 items-center'>
        <label className='h5 font-dark-grey mr2'>Manual Fare Collection</label>
        <Toggle state={values.isManualCollectEnabled} onClick={setManualCollectEnabled} />
      </div>
      <div className='flex flex-row mt2'>
        <div className='col-4 flex flex-column pr2'>
          <label className='h5'>Fare</label>
          <Field name='fare' placeholder='$0.00' className='rounded p1 border-light-grey4 h4' />
          <div className='font-red h6'>{touched.fare && errors.fare && <div>{errors.fare}</div>}</div>
        </div>
        <div className='col-4 pr2' />
        <div className='col-4 pr2' />
        <div className='col-4 pr2' />
      </div>
      <div className='font-dark-grey mt3'>
        <label className='mb0'>
          <input type='checkbox' checked={promoEnabled} onChange={togglePromoFare} className='mr1' />
          Promotional Fare (This fare is applied first)
        </label>
      </div>
      {promoEnabled && (
        <div className='flex flex-row mt2'>
          <div className='col-4 flex flex-column pr2'>
            <label className='h5'>Fare</label>
            <Field type='number' name='promoFare' placeholder='$0.00' className='rounded p1 border-light-grey4 h4' />
            <div className='font-red h6'>{touched.promoFare && errors.promoFare && <div>{errors.promoFare}</div>}</div>
          </div>
          <div className='col-4 flex flex-column pr2'>
            <label className='h5'>Number completed</label>
            <Field
              type='number'
              min='1'
              name='promoLimit'
              placeholder='0'
              className='rounded p1 border-light-grey4 h4'
            />
            <div className='font-red h6'>
              {touched.promoLimit && errors.promoLimit && <div>{errors.promoLimit}</div>}
            </div>
          </div>
          <div className='col-4 flex flex-column pr2'>
            <label className='h5'>Per</label>
            <Field
              name='promoDuration'
              placeholder='0'
              className='rounded p1 border-light-grey4 h4'
              //Rendering Per
              render={selectInput}
            />
            <div className='font-red h6'>
              {touched.promoDuration && errors.promoDuration && <div>{errors.promoDuration}</div>}
            </div>
          </div>
          <div className='col-4 pr2' />
        </div>
      )}
      <div className='bold font-dark-grey mt3'>Total Pricing Discount</div>
      <div className='flex flex-row mt2'>
        <div className='col-4  flex flex-column pr2'>
          <label className='h5'>Discount %</label>
          <Field
            name='discount'
            placeholder='Percentage as decimal 0.10 %'
            className='rounded p1 border-light-grey4 h4'
          />
          <div className='font-red h6'>{touched.discount && errors.discount && <div>{errors.discount}</div>}</div>
        </div>
        <div className='col-4  flex flex-column pr2'>
          <label className='h5'>Discount flat</label>
          <Field name='discountFlat' placeholder='$0.00' className='rounded p1 border-light-grey4 h4' />
          <div className='font-red h6'>
            {touched.discountFlat && errors.discountFlat && <div>{errors.discountFlat}</div>}
          </div>
        </div>
        <div className='col-4 pr2' />
        <div className='col-4 pr2' />
      </div>
      <div className='mt3 flex'>
        <div className='bold font-dark-grey'>Individual Pricing Discount</div>
        {pricingDiscounts && !!pricingDiscounts.length && (
          <>
            <ReactTooltip
              aria-haspopup='true'
              delayHide={200}
              delayUpdate={200}
              effect='solid'
              globalEventOff='click'
              id='service-pricing-discount-info'
              place='right'
            >
              <div>
                Discount value should be in decimal format
                <br />
                (e.g. 25% should be 0.25)
              </div>
            </ReactTooltip>
            <div
              className='bold cursor--pointer pl1'
              data-for='service-pricing-discount-info'
              data-tip='service-pricing-discount-info'
            >
              <TooltipIcon />
            </div>
          </>
        )}
      </div>
      {!(pricingDiscounts && !!pricingDiscounts.length) ? (
        <div className='flex font-dark-grey mt3'>
          <label>
            <input className='mr1' onChange={e => servicePricingDiscountChange(e)} type='checkbox' />
            Individual Pricing Discount
          </label>
        </div>
      ) : (
        <div className='col-12 mt1'>
          <div className='mbsc-grid no-padding'>
            <div className='mbsc-row'>
              <div className='mbsc-col-12'>
                <Button
                  ariaLabel='This action will disable all pricing discounts'
                  className='mbsc-flat-btn'
                  disabled={!pricingDiscounts.some(d => d.isEnabled)}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleDisableAllDiscount(e)}
                >
                  {'Disable all'}
                </Button>
              </div>
              {pricingDiscounts.map(({ componentName, discountType, discountValue, id, isEnabled }) => (
                <div className='mbsc-col-12 mbsc-col-md-4 mbsc-col-lg-3 pr2 py2' key={id}>
                  <div className='flex flex-column row-gap-8'>
                    <div>
                      <label className='h5' htmlFor={`discount-value-${id}`}>
                        {parseDiscountLabel(componentName)}
                      </label>
                      <input
                        className='border-light-grey4 h4 p1 rounded w100'
                        defaultValue={discountValue}
                        id={`discount-value-${id}`}
                        name={componentName}
                        type='number'
                        onChange={e => handleDiscountChange(e, id)}
                      />
                    </div>
                    <div>
                      <label className='h5' htmlFor={`discount-type-${id}`}>
                        Discount Type
                      </label>
                      <Select
                        inputId={`discount-type-${id}`}
                        isDisabled={true}
                        name={componentName}
                        options={discountTypeOptions}
                        value={discountTypeOptions.find(m => m.value === discountType)}
                        onChange={e => console.log(e)}
                      />
                    </div>
                    <div className='column-gap-8 flex items-center'>
                      <Toggle state={isEnabled} onClick={() => handleEnableDiscountToggle(!isEnabled, id)} />
                      <label className='h5 pb02' htmlFor={`enable-discount-${id}`}>
                        Enable Discount
                      </label>
                    </div>
                  </div>
                </div>
              ))}
              {originalPricingDiscountsData && originalPricingDiscountsData.length > 8 && (
                <div className='flex justify-end mbsc-col-12'>
                  <label className='bold cursor--pointer h5 italic underline' onClick={handleDiscountSlicing}>
                    {showLess ? 'See all' : 'See less'}
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className='bold font-dark-grey mt3'>Increase</div>
      <div className='flex flex-row mt2'>
        <div className='col-4 flex flex-column pr2'>
          <label className='h5'>Commission Flat</label>
          <Field name='commissionFlat' placeholder='$0.00' className='rounded p1 border-light-grey4 h4' />
          <div className='font-red h6'>
            {touched.commissionFlat && errors.commissionFlat && <div>{errors.commissionFlat}</div>}
          </div>
        </div>
        <div className='col-4  flex flex-column pr2'>
          <label className='h5'>Commission %</label>
          <Field
            name='commission'
            placeholder='Percentage as decimal 0.10 %'
            className='rounded p1 border-light-grey4 h4'
          />
          <div className='font-red h6'>{touched.commission && errors.commission && <div>{errors.commission}</div>}</div>
        </div>
        <div className='col-4  flex flex-column pr2'>
          <label className='h5'>State tax %</label>
          <Field
            name='stateTax'
            placeholder='Percentage as decimal 0.10 %'
            className='rounded p1 border-light-grey4 h4'
          />
          <div className='font-red h6'>{touched.stateTax && errors.stateTax && <div>{errors.stateTax}</div>}</div>
        </div>
        <div className='col-4  flex flex-column pr2'>
          <label className='h5'>Federal tax %</label>
          <Field
            name='federalTax'
            placeholder='Percentage as decimal 0.10 %'
            className='rounded p1 border-light-grey4 h4'
          />
          <div className='font-red h6'>{touched.federalTax && errors.federalTax && <div>{errors.federalTax}</div>}</div>
        </div>
      </div>
      <div className='flex flex-row mt2'>
        <div className='flex mt1 items-center'>
          <label className='h5 font-dark-grey mr2'>Remove cents in product pricing</label>
          <Toggle state={values.isRemoveCentsEnabled} onClick={setRemoveCentsEnabled} />
        </div>
      </div>
      <hr className='mt3' />
      <div className=' mt3'>
        <div className='bold font-dark-grey'>Attendant</div>
        <div className='flex flex-row flex-start items-center mt2'>
          <label className='mr3 h5'>Allow a Free Attendant / Additional Rider</label>
          <Field name='freeAttendant' render={freeAttendantField} />
        </div>
      </div>
    </form>
  )
}



// WEBPACK FOOTER //
// ./src/components/services/edit/ServicePricingBreakdown.tsx